import { Link, useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { applyTheme, getStoredTheme, type Theme } from '../lib/theme'

export function Brand() {
  return (
    <Link to="/" className="brand">
      <span className="brand-mark" aria-hidden="true">
        W
      </span>
      Wami
    </Link>
  )
}

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>('system')
  useEffect(() => setTheme(getStoredTheme()), [])

  const cycle = () => {
    const next: Theme = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system'
    setTheme(next)
    applyTheme(next)
  }

  const icon = theme === 'light' ? '☀' : theme === 'dark' ? '☾' : '◐'
  return (
    <button
      type="button"
      className="btn btn-text"
      onClick={cycle}
      aria-label={`Theme: ${theme}. Change theme.`}
      title={`Theme: ${theme}`}
      // Dropped on phones, where the OS preference is the one people actually set.
      style={{ display: 'var(--theme-toggle-display, inline-flex)' }}
    >
      <span aria-hidden="true" style={{ fontSize: '1.1rem' }}>
        {icon}
      </span>
    </button>
  )
}

export function MarketingHeader() {
  const path = useRouterState({ select: (s) => s.location.pathname })
  return (
    <header className="app-bar">
      <div className="container app-bar-inner">
        <Brand />
        <nav className="row gap-1" aria-label="Main">
          <Link
            to="/pricing"
            className="btn btn-text nav-hide-xs"
            aria-current={path === '/pricing' ? 'page' : undefined}
          >
            Pricing
          </Link>
          <Link
            to="/industries"
            className="btn btn-text nav-hide-sm"
            aria-current={path === '/industries' ? 'page' : undefined}
          >
            Industries
          </Link>
          <ThemeToggle />
          <Link to="/login" className="btn btn-text">
            Sign in
          </Link>
          <Link to="/signup" className="btn btn-filled">
            {/* The full label does not fit beside the rest of the nav on a phone. */}
            <span className="nav-label-full">Start free trial</span>
            <span className="nav-label-short">Start trial</span>
          </Link>
        </nav>
      </div>
    </header>
  )
}

export function Footer() {
  return (
    <footer style={{ borderTop: '1px solid var(--md-outline-variant)', marginTop: 'auto' }}>
      <div className="container section stack gap-6" style={{ paddingBlock: '48px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '32px',
          }}
        >
          <div className="stack gap-3">
            <Brand />
            <p className="body-small text-muted" style={{ maxWidth: '28ch' }}>
              WhatsApp operations for teams that outgrew a shared phone.
            </p>
          </div>
          <FooterColumn
            title="Product"
            links={[
              { to: '/pricing', label: 'Pricing' },
              { to: '/industries', label: 'Industries' },
              { to: '/compare', label: 'Compare' },
              { to: '/signup', label: 'Start free trial' },
            ]}
          />
          <FooterColumn
            title="Company"
            links={[{ to: '/trust', label: 'Trust' }]}
          />
          {/* These three pointed at '/' once, which is why they were pulled: a dead "DPA" link
              on a product arguing regulatory defensibility is worse than no link. They are back
              because the pages are real now — drafts pending counsel, and each says so at the
              top rather than pretending otherwise. */}
          <FooterColumn
            title="Legal"
            links={[
              { to: '/terms', label: 'Terms' },
              { to: '/privacy', label: 'Privacy' },
              { to: '/dpa', label: 'DPA' },
            ]}
          />
        </div>
        <hr className="divider" />
        <p className="body-small text-muted">
          © {new Date().getFullYear()} Wami. Prices shown exclude applicable taxes unless stated.
        </p>
      </div>
    </footer>
  )
}

function FooterColumn({ title, links }: { title: string; links: { to: string; label: string }[] }) {
  return (
    <div className="stack gap-2">
      <h3 className="title-small">{title}</h3>
      {links.map((l, i) => (
        <Link key={`${l.label}-${i}`} to={l.to} className="body-small text-muted">
          {l.label}
        </Link>
      ))}
    </div>
  )
}

/**
 * What to say when "See a live demo" fails.
 *
 * All four marketing pages discarded the real error and printed "please try again shortly."
 * The usual cause is a 503 — the shared demo workspace has not been seeded on this deployment —
 * which never clears on its own, so the visitor retries a condition that cannot resolve. This
 * shows what the server actually said and offers the route that does work.
 */
export function DemoStartError({ error }: { error: unknown }) {
  const message =
    error instanceof Error && error.message ? error.message : 'The live demo could not be reached.'
  return (
    <div className="stack gap-2">
      <p className="body-small" style={{ color: 'var(--md-error)' }}>
        {message}
      </p>
      <div>
        <Link to="/signup" className="btn btn-outlined">
          Start a free trial instead
        </Link>
      </div>
    </div>
  )
}

export function MarketingShell({ children }: { children: React.ReactNode }) {
  return (
    // `editorial` scopes the warm paper theme to the marketing site; the signed-in app
    // keeps the Material palette, so the two never fight over the same tokens.
    <div className="editorial stack" style={{ minHeight: '100dvh' }}>
      <MarketingHeader />
      <main className="grow">{children}</main>
      <Footer />
    </div>
  )
}
